import { supabase } from "@/integrations/supabase/client";
import { workspaceInsertFields, getActiveWorkspaceId } from "@/utils/workspace-helpers";

/**
 * Populates clean defaults (currencies, categories, contract types).
 */
export async function populateDefaults(userId: string) {
  const wsFields = workspaceInsertFields();
  const workspaceId = wsFields.workspace_id ?? null;

  const defaultCurrencies = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  ];

  // Fetch already-existing currencies for this workspace (or user as fallback)
  let existingCurrencyQuery = supabase.from('currencies').select('id, code');
  if (workspaceId) existingCurrencyQuery = existingCurrencyQuery.eq('workspace_id', workspaceId);
  else existingCurrencyQuery = existingCurrencyQuery.eq('user_id', userId);
  const { data: existingCurrencies } = await existingCurrencyQuery;

  const existingCurrencyCodes = new Set((existingCurrencies ?? []).map(c => c.code.toUpperCase()));
  const currenciesToInsert = defaultCurrencies.filter(c => !existingCurrencyCodes.has(c.code.toUpperCase()));

  let insertedCurrencies: { id: string; code: string }[] = [];
  if (currenciesToInsert.length > 0) {
    const { data, error } = await supabase
      .from('currencies')
      .insert(currenciesToInsert.map(c => ({ ...c, user_id: userId, ...wsFields })))
      .select('id, code');
    if (error) {
      console.error('Error populating default currencies:', error);
    } else {
      insertedCurrencies = data ?? [];
    }
  }
  const allCurrencies = [...(existingCurrencies ?? []), ...insertedCurrencies];

  // Categories — dedupe by name
  const defaultCategories = [
    { name: 'IT & Software', description: 'Software licenses, cloud services, and IT infrastructure' },
    { name: 'Office & Facilities', description: 'Office rent, utilities, and facility management' },
    { name: 'Marketing', description: 'Marketing and advertising services' },
    { name: 'Professional Services', description: 'Consulting, legal, and accounting services' },
    { name: 'Equipment & Hardware', description: 'Equipment leases and hardware purchases' },
  ];
  let existingCatQuery = supabase.from('categories').select('name');
  if (workspaceId) existingCatQuery = existingCatQuery.eq('workspace_id', workspaceId);
  else existingCatQuery = existingCatQuery.eq('user_id', userId);
  const { data: existingCats } = await existingCatQuery;
  const existingCatNames = new Set((existingCats ?? []).map((c: any) => c.name.toLowerCase()));
  const catsToInsert = defaultCategories.filter(c => !existingCatNames.has(c.name.toLowerCase()));
  if (catsToInsert.length > 0) {
    await supabase
      .from('categories')
      .insert(catsToInsert.map(c => ({ ...c, user_id: userId, ...wsFields })));
  }

  // Contract types — dedupe by name
  const defaultContractTypes = [
    { name: 'Software License', description: 'Software subscription or license', direction: 'outgoing', auto_renews: true, renewal_interval: 1, renewal_unit: 'Year' },
    { name: 'Service Agreement', description: 'General service contract', direction: 'outgoing', auto_renews: true, renewal_interval: 1, renewal_unit: 'Year' },
    { name: 'Office Lease', description: 'Office space rental', direction: 'outgoing', auto_renews: false, renewal_interval: 1, renewal_unit: 'Year' },
    { name: 'Customer Contract', description: 'Contract with customer', direction: 'incoming', auto_renews: true, renewal_interval: 1, renewal_unit: 'Year' },
  ];
  let existingCtQuery = supabase.from('contract_types').select('name');
  if (workspaceId) existingCtQuery = existingCtQuery.eq('workspace_id', workspaceId);
  else existingCtQuery = existingCtQuery.eq('user_id', userId);
  const { data: existingCts } = await existingCtQuery;
  const existingCtNames = new Set((existingCts ?? []).map((c: any) => c.name.toLowerCase()));
  const ctsToInsert = defaultContractTypes.filter(c => !existingCtNames.has(c.name.toLowerCase()));
  if (ctsToInsert.length > 0) {
    await supabase
      .from('contract_types')
      .insert(ctsToInsert.map(ct => ({ ...ct, user_id: userId, ...wsFields })));
  }

  // Exchange rates — only insert when missing for this currency pair
  const findCurr = (code: string) => allCurrencies.find(c => c.code.toUpperCase() === code)?.id;
  const usdId = findCurr('USD');
  const eurId = findCurr('EUR');
  const gbpId = findCurr('GBP');
  const nokId = findCurr('NOK');
  if (usdId && eurId && gbpId && nokId) {
    const desiredRates = [
      { base_currency_id: usdId, target_currency_id: eurId, rate: 0.92 },
      { base_currency_id: usdId, target_currency_id: gbpId, rate: 0.79 },
      { base_currency_id: usdId, target_currency_id: nokId, rate: 10.85 },
      { base_currency_id: eurId, target_currency_id: usdId, rate: 1.09 },
      { base_currency_id: eurId, target_currency_id: gbpId, rate: 0.86 },
      { base_currency_id: eurId, target_currency_id: nokId, rate: 11.81 },
    ];
    const { data: existingRates } = await supabase
      .from('exchange_rates')
      .select('base_currency_id, target_currency_id')
      .in('base_currency_id', [usdId, eurId])
      .in('target_currency_id', [usdId, eurId, gbpId, nokId]);
    const existingKey = new Set((existingRates ?? []).map((r: any) => `${r.base_currency_id}:${r.target_currency_id}`));
    const ratesToInsert = desiredRates.filter(r => !existingKey.has(`${r.base_currency_id}:${r.target_currency_id}`));
    if (ratesToInsert.length > 0) {
      await supabase.from('exchange_rates').insert(ratesToInsert);
    }
  }

  if (allCurrencies.length > 0) {
    const { defaultStatusColors } = await import("@/hooks/useStatusColors");
    const usdCurrency = allCurrencies.find(c => c.code === 'USD');
    await supabase
      .from('user_preferences')
      .update({
        default_currency_id: usdCurrency?.id || allCurrencies[0].id,
        statusColors: JSON.stringify(defaultStatusColors),
      } as any)
      .eq('user_id', userId);
  }
}

/**
 * Seeds realistic demo data for a new user.
 */
export async function populateDemoData(userId: string) {
  const wsFields = workspaceInsertFields();
  const workspaceId = getActiveWorkspaceId();
  if (!workspaceId) return;

  // Check if demo data already exists
  const { data: existingDemo } = await (supabase
    .from('contracts')
    .select('id') as any)
    .eq('user_id', userId)
    .eq('is_demo_data', true)
    .limit(1);

  if (existingDemo && existingDemo.length > 0) return;

  // 1. Create demo suppliers
  const demoSuppliers = [
    { name: 'Acme Software AS', organisation_number: '912345678', email: 'post@acmesoftware.no', phone: '+47 22 33 44 55', address: 'Teknologiveien 12', city: 'Oslo', postal_code: '0167', country: 'Norway', website: 'https://acmesoftware.no' },
    { name: 'CloudHost Solutions', organisation_number: '923456789', email: 'info@cloudhost.com', phone: '+47 33 44 55 66', address: 'Serverparken 5', city: 'Bergen', postal_code: '5003', country: 'Norway', website: 'https://cloudhost.com' },
    { name: 'Office Supplies Ltd', organisation_number: '934567890', email: 'orders@officesupplies.no', phone: '+47 44 55 66 77', address: 'Kontorvegen 88', city: 'Trondheim', postal_code: '7010', country: 'Norway' },
    { name: 'Marketing Pro Agency', organisation_number: '945678901', email: 'hello@marketingpro.no', phone: '+47 55 66 77 88', address: 'Reklamegata 3', city: 'Stavanger', postal_code: '4006', country: 'Norway', website: 'https://marketingpro.no' },
    { name: 'SecureIT Consulting', organisation_number: '956789012', email: 'contact@secureit.no', phone: '+47 66 77 88 99', address: 'Sikkerhetsvegen 21', city: 'Oslo', postal_code: '0250', country: 'Norway', website: 'https://secureit.no' },
  ];

  const { data: suppliers, error: supErr } = await supabase
    .from('suppliers')
    .insert(demoSuppliers.map(s => ({ ...s, user_id: userId, workspace_id: workspaceId, is_demo_data: true, is_active: true } as any)))
    .select();

  if (supErr || !suppliers) {
    console.error('Error creating demo suppliers:', supErr);
    return;
  }

  // 2. Create demo contacts
  const contactNames = [
    { name: 'Erik Hansen', email: 'erik@acmesoftware.no', role: 'Account Manager', phone: '+47 900 11 222' },
    { name: 'Maria Johansen', email: 'maria@cloudhost.com', role: 'Technical Lead', phone: '+47 900 33 444' },
    { name: 'Lars Olsen', email: 'lars@officesupplies.no', role: 'Sales Representative', phone: '+47 900 55 666' },
    { name: 'Karin Nilsen', email: 'karin@marketingpro.no', role: 'Project Manager', phone: '+47 900 77 888' },
    { name: 'Anders Berg', email: 'anders@secureit.no', role: 'Security Consultant', phone: '+47 900 99 000' },
  ];

  const { data: contacts, error: contErr } = await supabase
    .from('contact_persons')
    .insert(contactNames.map((c, i) => ({
      ...c,
      supplier_id: suppliers[i].id,
      user_id: userId,
      is_demo_data: true,
      ...wsFields,
    } as any)))
    .select();

  if (contErr) {
    console.error('Error creating demo contacts:', contErr);
  }

  // 3. Get categories and contract types
  const { data: categories } = await supabase
    .from('categories')
    .select('id, name')
    .eq('user_id', userId);

  const { data: contractTypes } = await supabase
    .from('contract_types')
    .select('id, name')
    .eq('user_id', userId);

  const { data: currencies } = await supabase
    .from('currencies')
    .select('id, code')
    .eq('user_id', userId);

  const findCategory = (n: string) => categories?.find(c => c.name.includes(n))?.id || null;
  const findType = (n: string) => contractTypes?.find(ct => ct.name.includes(n))?.id || null;
  const findCurrency = (c: string) => currencies?.find(cur => cur.code === c)?.id || null;

  const now = new Date();
  const daysAgo = (d: number) => new Date(now.getTime() - d * 86400000).toISOString().split('T')[0];
  const daysFromNow = (d: number) => new Date(now.getTime() + d * 86400000).toISOString().split('T')[0];

  // 4. Create demo contracts
  const demoContracts = [
    {
      supplier_name: 'Acme Software AS', supplier_id: suppliers[0].id, organisationnumber: '912345678',
      type: 'Software License', expected_cost: 4500, payment_frequency: 'Monthly',
      signed_date: daysAgo(365), expiry_date: daysFromNow(180), importance: 3,
      reminder_period: '30 days', reminder_frequency: 'Once',
      category_id: findCategory('IT'), contract_type_id: findType('Software'), currency_id: findCurrency('NOK'),
    },
    {
      supplier_name: 'Acme Software AS', supplier_id: suppliers[0].id, organisationnumber: '912345678',
      type: 'Service Agreement', expected_cost: 12000, payment_frequency: 'Yearly',
      signed_date: daysAgo(200), expiry_date: daysFromNow(165), importance: 2,
      reminder_period: '30 days', reminder_frequency: 'Once',
      category_id: findCategory('IT'), contract_type_id: findType('Service'), currency_id: findCurrency('NOK'),
    },
    {
      supplier_name: 'CloudHost Solutions', supplier_id: suppliers[1].id, organisationnumber: '923456789',
      type: 'Service Agreement', expected_cost: 2200, payment_frequency: 'Monthly',
      signed_date: daysAgo(500), expiry_date: daysFromNow(90), importance: 4,
      reminder_period: '14 days', reminder_frequency: 'Once',
      category_id: findCategory('IT'), contract_type_id: findType('Service'), currency_id: findCurrency('USD'),
    },
    {
      supplier_name: 'Office Supplies Ltd', supplier_id: suppliers[2].id, organisationnumber: '934567890',
      type: 'Service Agreement', expected_cost: 850, payment_frequency: 'Quarterly',
      signed_date: daysAgo(730), expiry_date: daysFromNow(45), importance: 1,
      reminder_period: '30 days', reminder_frequency: 'Once',
      category_id: findCategory('Office'), contract_type_id: findType('Service'), currency_id: findCurrency('NOK'),
    },
    {
      supplier_name: 'Marketing Pro Agency', supplier_id: suppliers[3].id, organisationnumber: '945678901',
      type: 'Service Agreement', expected_cost: 15000, payment_frequency: 'Monthly',
      signed_date: daysAgo(120), expiry_date: daysFromNow(245), importance: 3,
      reminder_period: '30 days', reminder_frequency: 'Once',
      category_id: findCategory('Marketing'), contract_type_id: findType('Service'), currency_id: findCurrency('EUR'),
    },
    {
      supplier_name: 'SecureIT Consulting', supplier_id: suppliers[4].id, organisationnumber: '956789012',
      type: 'Service Agreement', expected_cost: 8500, payment_frequency: 'Yearly',
      signed_date: daysAgo(60), expiry_date: daysFromNow(305), importance: 5,
      reminder_period: '30 days', reminder_frequency: 'Once',
      category_id: findCategory('Professional'), contract_type_id: findType('Service'), currency_id: findCurrency('NOK'),
    },
    {
      supplier_name: 'CloudHost Solutions', supplier_id: suppliers[1].id, organisationnumber: '923456789',
      type: 'Software License', expected_cost: 500, payment_frequency: 'Monthly',
      signed_date: daysAgo(400), expiry_date: daysAgo(30), importance: 2,
      reminder_period: '30 days', reminder_frequency: 'Once',
      category_id: findCategory('IT'), contract_type_id: findType('Software'), currency_id: findCurrency('USD'),
    },
    {
      supplier_name: 'Marketing Pro Agency', supplier_id: suppliers[3].id, organisationnumber: '945678901',
      type: 'Customer Contract', expected_cost: 7500, payment_frequency: 'Monthly',
      signed_date: daysAgo(90), expiry_date: daysFromNow(275), importance: 4,
      reminder_period: '14 days', reminder_frequency: 'Once',
      category_id: findCategory('Marketing'), contract_type_id: findType('Customer'), currency_id: findCurrency('NOK'),
    },
  ];

  const { data: createdContracts, error: contractErr } = await supabase
    .from('contracts')
    .insert(demoContracts.map(c => ({
      ...c,
      user_id: userId,
      is_demo_data: true,
      ...wsFields,
    } as any)))
    .select();

  if (contractErr) {
    console.error('Error creating demo contracts:', contractErr);
    return;
  }

  // 5. Link contacts to contracts
  if (createdContracts && contacts) {
    const contractContacts: any[] = [];
    createdContracts.forEach((contract: any) => {
      const supplierIdx = suppliers.findIndex(s => s.id === contract.supplier_id);
      if (supplierIdx >= 0 && contacts[supplierIdx]) {
        contractContacts.push({
          contract_id: contract.id,
          contact_id: contacts[supplierIdx].id,
          user_id: userId,
          ...wsFields,
        });
      }
    });

    if (contractContacts.length > 0) {
      await supabase.from('contract_contacts').insert(contractContacts);
    }
  }

  // 6. Mark user as having demo data
  await supabase
    .from('user_preferences')
    .update({ has_demo_data: true } as any)
    .eq('user_id', userId);
}
